import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2, ShieldCheck, FileText, Package } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatEUR = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' }); }
  catch { return iso; }
};

const QuoteAcceptancePage = () => {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | already | error | submitting | done
  const [error, setError] = useState('');
  const [quote, setQuote] = useState(null);
  const [signedName, setSignedName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/quotes/view/${token}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Errore nel recupero del preventivo');
        setQuote(data);
        if (data.already_accepted) {
          setState('already');
        } else {
          setState('ready');
          setSignedName(data.customer_name || '');
        }
      } catch (e) {
        setError(e.message);
        setState('error');
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      setError('Devi confermare la lettura e accettazione delle Condizioni Generali di Contratto.');
      return;
    }
    if (!signedName || signedName.trim().length < 3) {
      setError('Inserisci nome e cognome (minimo 3 caratteri) per firmare l\'accettazione.');
      return;
    }
    setError('');
    setState('submitting');
    try {
      const res = await fetch(`${BACKEND_URL}/api/public/quotes/accept/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signed_name: signedName.trim(), terms_accepted: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Errore durante l\'accettazione');
      setResult(data);
      setState('done');
    } catch (e) {
      setError(e.message);
      setState('ready');
    }
  };

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-fuchsia-50">
        <div className="flex items-center gap-3 text-purple-700 font-medium">
          <Loader2 className="w-6 h-6 animate-spin" />
          Caricamento preventivo…
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-fuchsia-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link non valido</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/" className="inline-block px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">
            Torna al sito
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-fuchsia-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <img src="https://customer-assets.emergentagent.com/job_mixology-admin/artifacts/pfeg5nio_iltuoamaro-black%401000x.webp" alt="Il Tuo Amaro" className="h-10 mx-auto mb-3" />
          <h1 className="text-3xl font-bold text-gray-900">Accettazione preventivo</h1>
          <p className="text-gray-600 mt-1">Preventivo n. <strong>#{quote?.quote_number}</strong></p>
        </div>

        {/* Already accepted state */}
        {state === 'already' && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center" data-testid="already-accepted">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Preventivo già accettato ✓</h2>
            <p className="text-gray-600">Il preventivo <strong>#{quote?.quote_number}</strong> risulta già confermato.</p>
            {quote?.acceptance?.accepted_at && (
              <p className="text-sm text-gray-500 mt-3">Firmato da <strong>{quote.acceptance.signed_name}</strong> il {formatDate(quote.acceptance.accepted_at)}</p>
            )}
            <p className="text-gray-600 mt-6">Il nostro team commerciale ti contatterà a breve. Nel frattempo puoi tornare al sito.</p>
            <Link to="/" className="inline-block mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">
              Torna al sito
            </Link>
          </div>
        )}

        {/* Success state */}
        {state === 'done' && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center" data-testid="acceptance-success">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Accettazione confermata! 🎉</h2>
            <p className="text-gray-600 mb-4">Grazie <strong>{result?.signed_name}</strong>. Abbiamo registrato la tua accettazione del preventivo <strong>#{quote?.quote_number}</strong>.</p>
            <div className="bg-purple-50 rounded-lg p-4 my-6 text-sm text-purple-900">
              📧 Riceverai a breve un'email di conferma. Il nostro team commerciale ti contatterà per avviare la produzione.
            </div>
            <Link to="/" className="inline-block px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium">
              Torna al sito
            </Link>
          </div>
        )}

        {/* Ready to accept */}
        {(state === 'ready' || state === 'submitting') && (
          <>
            {/* Riepilogo preventivo */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
              <div className="px-6 py-4 border-b bg-gradient-to-r from-purple-50 to-fuchsia-50 flex items-center gap-3">
                <FileText className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-bold text-gray-900">Riepilogo</h2>
                {quote?.expiry_date && (
                  <span className="ml-auto text-xs text-gray-500">Valido fino al {formatDate(quote.expiry_date)}</span>
                )}
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Cliente</p>
                    <p className="font-semibold text-gray-900">{quote?.company_name || quote?.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="font-semibold text-gray-900">{quote?.customer_email}</p>
                  </div>
                </div>

                {/* Prodotti */}
                {(quote?.items || []).length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold text-gray-700">Prodotto</th>
                          <th className="text-right px-3 py-2 font-semibold text-gray-700">Qty</th>
                          <th className="text-right px-3 py-2 font-semibold text-gray-700">Prezzo</th>
                          <th className="text-right px-3 py-2 font-semibold text-gray-700">Totale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quote.items.map((it, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-800">{it.description}</div>
                              <div className="text-xs text-gray-500">{it.format}</div>
                            </td>
                            <td className="text-right px-3 py-2 tabular-nums">{it.quantity}</td>
                            <td className="text-right px-3 py-2 tabular-nums">{formatEUR(it.unit_price)}</td>
                            <td className="text-right px-3 py-2 tabular-nums font-semibold">{formatEUR(it.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Totali */}
                <div className="border-t pt-4 space-y-1 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotale (IVA esclusa)</span>
                    <span className="tabular-nums">{formatEUR(quote?.subtotal)}</span>
                  </div>
                  {Number(quote?.shipping_cost || 0) > 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>Spedizione (IVA esclusa)</span>
                      <span className="tabular-nums">{formatEUR(quote?.shipping_cost)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-600">
                    <span>IVA 22%</span>
                    <span className="tabular-nums">{formatEUR(quote?.vat_amount)}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold text-purple-900 pt-2 border-t mt-2">
                    <span>Totale (IVA inclusa)</span>
                    <span className="tabular-nums">{formatEUR(quote?.total)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Form accettazione */}
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6" data-testid="acceptance-form">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-bold text-gray-900">Firma digitale</h2>
              </div>

              <label className="flex items-start gap-3 p-3 border-2 border-purple-100 rounded-lg mb-4 cursor-pointer hover:bg-purple-50 transition-colors">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-purple-600"
                  data-testid="terms-checkbox"
                />
                <span className="text-sm text-gray-800">
                  Dichiaro di aver letto e accetto integralmente le{' '}
                  <a href="/termini" target="_blank" rel="noreferrer" className="text-purple-700 font-semibold underline">
                    Condizioni Generali di Contratto
                  </a>{' '}
                  <span className="text-red-500">*</span>
                </span>
              </label>

              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Firma con nome e cognome <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={signedName}
                  onChange={(e) => setSignedName(e.target.value)}
                  placeholder="Es. Mario Rossi"
                  data-testid="signed-name-input"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-base"
                />
                <p className="text-xs text-gray-500 mt-1">La firma digitale ha valore legale di accettazione ai sensi del Codice Civile.</p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="acceptance-error">
                  ⚠️ {error}
                </div>
              )}

              <button
                type="submit"
                disabled={state === 'submitting' || !termsAccepted || !signedName.trim()}
                data-testid="btn-accept-quote"
                className="w-full flex items-center justify-center gap-2 py-4 rounded-lg font-bold text-white text-base
                  bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700
                  disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg"
              >
                {state === 'submitting' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Invio in corso…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5" /> Conferma accettazione preventivo
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 mt-4 text-center flex items-center justify-center gap-1">
                <Package className="w-3 h-3" /> Al termine avvii la produzione entro 20 giorni lavorativi
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default QuoteAcceptancePage;
